// A production as the v2 screens summarise it: cover stills, the latest episode, the facts its department
// steps are read from, and what it has cost. `summaryReads` reads a few productions at a time (GET
// /production-summaries) and keeps them a moment so Home, the sidebar and Productions share them. `factsOf` reads the same
// facts from a loaded production.
import type { Api } from "../api.ts";
import { pool } from "../lib.ts";
import { shotGenerations } from "../looseShot.ts";
import { isDefaultScene } from "../scenes.ts";
import type { AssetUrl, Element, Episode, Project, TakesResponse } from "../types.ts";
import { keepAssetUrls } from "../useAsset.ts";
import type { ProjectData } from "../useProject.ts";
import { NO_FACTS, type StepFacts } from "./steps.ts";
import type { ApiEpisodeSteps } from "./stepsApi.ts";

export interface Summary {
  project: Project;
  episodes: Episode[];
  latest: Episode | null;
  /** asset version ids of picked keyframes, in cut order (up to four) */
  covers: string[];
  /** the first picked video in cut order: what the card plays on hover */
  clip: string | null;
  shots: number;
  facts: StepFacts;
  /** model cost of every episode (archived too) added up */
  spent: SpentUsd;
  lastActivity: string | null;
}

export interface SpentUsd {
  totalUsd: number;
  draftUsd: number;
  finalUsd: number;
}

/** The counts the steps are read from, out of elements and the picks of an episode's shots. */
export function countFacts(input: {
  elements: readonly Pick<Element, "subtype" | "archived_at">[];
  shots: number;
  picks: readonly Pick<TakesResponse, "current_selection">[];
  finals?: number;
  scriptRevisions?: number;
  animaticSignedOff?: boolean;
  animaticWaiting?: boolean;
}): StepFacts {
  const live = input.elements.filter((e) => !e.archived_at);
  const n = (s: string) => live.filter((e) => e.subtype === s).length;
  const picked = (kind: string) =>
    input.picks.filter((t) =>
      t.current_selection.some((s) => s.kind === kind && (s.asset_version_id || s.take_id)),
    ).length;
  return {
    ...NO_FACTS,
    scriptRevisions: input.scriptRevisions ?? 0,
    shots: input.shots,
    characters: n("character"),
    locations: n("location"),
    props: n("prop"),
    keyframesPicked: picked("keyframe"),
    draftsPicked: picked("video"),
    finalsPicked: input.finals ?? 0,
    soundPicked: picked("sfx"),
    animaticSignedOff: !!input.animaticSignedOff,
    animaticWaiting: !!input.animaticWaiting,
  };
}

/** The facts of one episode of a loaded production (the board). */
export function factsOf(d: ProjectData, episodeId: string, extra: Partial<StepFacts> = {}): StepFacts {
  const shotIds = (d.scenes.get(episodeId) ?? []).flatMap((s) => (d.shots.get(s.id) ?? []).map((x) => x.id));
  const picks = shotIds.map((id) => d.takes.get(id)).filter((t): t is TakesResponse => !!t);
  const finals = shotIds.filter((id) =>
    shotGenerations(d, id).some((g) => g.tier === "final" && g.status === "succeeded"),
  ).length;
  // the default scene counts only when it holds shots (as the board lists them)
  const scenes = (d.scenes.get(episodeId) ?? []).filter(
    (s) => !isDefaultScene(s) || (d.shots.get(s.id) ?? []).length > 0,
  );
  const withMusic = scenes.filter((s) =>
    d.sceneTakes
      .get(s.id)
      ?.current_selection.some((x) => x.kind === "music" && (x.asset_version_id || x.take_id)),
  ).length;
  return {
    ...countFacts({ elements: d.elements, shots: shotIds.length, picks, finals }),
    scenes: scenes.length,
    musicScenes: withMusic,
    ...extra,
  };
}

/** One production of GET /production-summaries. */
export interface ApiProductionSummary {
  project_id: string;
  episodes: Episode[];
  latest_episode_id: string | null;
  shots: number;
  covers: string[];
  clip: string | null;
  characters: number;
  locations: number;
  props: number;
  script_revisions: number;
  keyframes_picked: number;
  drafts_picked: number;
  sound_picked: number;
  animatic_signed_off: boolean;
  spent: { total_usd: number; draft_usd: number; final_usd: number };
  last_selected_at: string | null;
  steps: ApiEpisodeSteps | null;
}

export function summaryFromApi(project: Project, a: ApiProductionSummary): Summary {
  const created = (project as { created_at?: string }).created_at ?? null;
  return {
    project,
    episodes: a.episodes,
    latest: a.episodes.find((e) => e.id === a.latest_episode_id) ?? null,
    covers: a.covers,
    clip: a.clip,
    shots: a.shots,
    facts: {
      ...NO_FACTS,
      scriptRevisions: a.script_revisions,
      shots: a.shots,
      characters: a.characters,
      locations: a.locations,
      props: a.props,
      keyframesPicked: a.keyframes_picked,
      draftsPicked: a.drafts_picked,
      soundPicked: a.sound_picked,
      animaticSignedOff: a.animatic_signed_off,
    },
    spent: { totalUsd: a.spent.total_usd, draftUsd: a.spent.draft_usd, finalUsd: a.spent.final_usd },
    lastActivity:
      !a.last_selected_at || (created && created > a.last_selected_at) ? created : a.last_selected_at,
  };
}

export interface LoadedSummary {
  summary: Summary;
  steps: ApiEpisodeSteps | null;
}

/** How many productions one read asks for, and how many reads run at once: the step rules cost per episode. */
const CHUNK = 6;
const READS = 2;
/** Long enough for the screens of one view to share a read; a step signed off shows on the next screen. */
const TTL = 5_000;

const cache = new Map<string, { at: number; value: Promise<ApiProductionSummary | undefined> }>();

/** On sign-out: what one person may see never reaches the next on this tab. */
export const forgetSummaries = (): void => cache.clear();

/**
 * The summary of each of these productions, in their order, answering as the reads come in: the first productions
 * first, so a list fills from the top. A production asked for a moment ago shares that read; `fresh` reads it anew (and
 * keeps that). Undefined: the server does not answer for it. A failed read rejects and is not kept.
 */
export function summaryReads(
  api: Api,
  ws: string,
  projects: readonly Project[],
  opts: { fresh?: boolean } = {},
): Promise<LoadedSummary | undefined>[] {
  const now = Date.now();
  const keyOf = (p: Project) => `${ws}:${p.id}`;
  const usable = (p: Project) => {
    const hit = cache.get(keyOf(p));
    return !opts.fresh && hit && now - hit.at < TTL;
  };
  const missing = projects.filter((p) => !usable(p));
  const tasks: (() => Promise<unknown>)[] = [];
  for (let i = 0; i < missing.length; i += CHUNK) {
    const chunk = missing.slice(i, i + CHUNK);
    let start = () => {};
    const go = new Promise<void>((resolve) => {
      start = resolve;
    });
    const answer = go
      .then(() =>
        api.request<{ items: ApiProductionSummary[]; cover_urls?: (AssetUrl & { id: string })[] }>(
          "GET",
          `/workspaces/${ws}/production-summaries`,
          { query: { projectIds: chunk.map((p) => p.id).join(",") } },
        ),
      )
      .then((r) => {
        keepAssetUrls(ws, r.cover_urls ?? []);
        return new Map(r.items.map((s) => [s.project_id, s]));
      });
    for (const p of chunk) {
      const key = keyOf(p);
      const value = answer.then((m) => m.get(p.id));
      cache.set(key, { at: now, value });
      value.catch(() => {
        if (cache.get(key)?.value === value) cache.delete(key);
      });
    }
    tasks.push(() => {
      start();
      return answer.catch(() => undefined);
    });
  }
  void pool(READS, tasks);
  return projects.map(async (p) => {
    const a = await cache.get(keyOf(p))?.value;
    return a ? { summary: summaryFromApi(p, a), steps: a.steps } : undefined;
  });
}
